import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Shield, CheckCircle, Award, Settings, Zap, Eye } from 'lucide-react';
import { useSEO } from '../../utils/seo';

export default function Services() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'Hizmetlerimiz - ISO Belgelendirme, Denetim ve Teknik Kontroller | IQCERT',
    description: 'IQCERT belgelendirme, denetim ve danışmanlık hizmetleri. ISO 9001, ISO 14001, ISO 45001, teknik kontroller, enerji verimliliği ve özel denetim hizmetleri sunuyoruz.',
    keywords: 'ISO belgelendirme, yönetim sistemleri, teknik kontroller, denetim hizmetleri, enerji verimliliği, asansör kontrolü',
    canonical: `${siteUrl}/services`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${siteUrl}/services#servicelist`,
      name: 'IQCERT Hizmetleri',
      description: 'Belgelendirme, denetim ve danışmanlık hizmetleri',
      itemListElement: [
        {
          '@type': 'Service',
          position: 1,
          name: 'Belgelendirme Hizmetleri',
          description: 'Sürdürülebilir turizm, personel ve vegan belgelendirme hizmetleri',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          serviceType: 'Certification Services',
          areaServed: 'TR'
        },
        {
          '@type': 'Service',
          position: 2,
          name: 'Yönetim Sistemleri Belgelendirme',
          description: 'ISO 9001, ISO 14001, ISO 45001 ve diğer ISO standartları belgelendirme',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          serviceType: 'ISO Certification',
          areaServed: 'TR'
        },
        {
          '@type': 'Service',
          position: 3,
          name: 'Teknik Kontroller',
          description: 'Asansör, yürüyen merdiven, iş ekipmanları periyodik kontrolleri',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          serviceType: 'Technical Inspection',
          areaServed: 'TR'
        },
        {
          '@type': 'Service',
          position: 4,
          name: 'Denetim ve Gözetim Hizmetleri',
          description: 'Yükleme öncesi gözetim, ikinci taraf denetim, hijyen denetimleri',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          serviceType: 'Audit Services',
          areaServed: 'TR'
        },
        {
          '@type': 'Service',
          position: 5,
          name: 'Enerji Verimliliği',
          description: 'Enerji kimlik belgesi, enerji etüdü ve verimlilik danışmanlığı',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          serviceType: 'Energy Efficiency Consulting',
          areaServed: 'TR'
        },
        {
          '@type': 'Service',
          position: 6,
          name: 'Özel Denetimler',
          description: 'Okul güvenliği, yeşil bina, otel denetimleri ve sınıflandırma',
          provider: {
            '@id': `${siteUrl}/#organization`
          },
          serviceType: 'Special Audits',
          areaServed: 'TR'
        }
      ]
    }
  });

  const serviceCategories = [
    {
      icon: Shield,
      title: 'Belgelendirme Hizmetleri',
      description: 'Sürdürülebilir turizm, personel ve vegan belgelendirme hizmetleri',
      color: 'bg-blue-500',
      services: [
        'Sürdürülebilir Turizm Belgelendirmesi',
        'GSTC (Global Sustainable Tourism Council) Sertifikası',
        'Personel Belgelendirme Hizmetleri',
        'Vegan Ürün Belgelendirmesi',
        'Organik Ürün Belgelendirmesi',
        'Helal Gıda Belgelendirmesi'
      ]
    },
    {
      icon: Settings,
      title: 'Yönetim Sistemleri Belgelendirme',
      description: 'ISO standartlarına uygun yönetim sistemi belgelendirmeleri',
      color: 'bg-green-500',
      services: [
        'ISO 9001 Kalite Yönetim Sistemi',
        'ISO 14001 Çevre Yönetim Sistemi',
        'ISO 45001 İş Sağlığı ve Güvenliği Yönetim Sistemi',
        'ISO 50001 Enerji Yönetim Sistemi',
        'ISO 10002 Müşteri Memnuniyeti Yönetim Sistemi',
        'ISO 27001 Bilgi Güvenliği Yönetim Sistemi',
        'ISO 27701 Kişisel Verilerin Güvenliği',
        'ISO 31000 Risk Yönetim Sistemi',
        'ISO 39001 Karayolu Trafik Güvenliği Yönetim Sistemi',
        'ISO 28001 Tedarik Zinciri Güvenlik Yönetim Sistemi',
        'ISO 22301 İş Sürekliliği Yönetim Sistemi'
      ]
    },
    {
      icon: CheckCircle,
      title: 'Teknik Kontroller',
      description: 'Periyodik kontroller ve teknik denetim hizmetleri',
      color: 'bg-orange-500',
      services: [
        'Asansör Periyodik Kontrolü',
        'Yürüyen Merdiven Periyodik Kontrolü',
        'Fotovoltaik Sistem Kontrolleri',
        'İş Ekipmanları Periyodik Kontrolü',
        'Basınçlı Kaplar ve Kazanlar Kontrolleri',
        'Yangından Korunma Sistemi Kontrolleri',
        'Kaldırma ve İletme Makineleri Kontrolleri',
        'Elektrik Tesisatları Kontrolleri',
        'İş Makineleri Periyodik Kontrolü',
        'Tezgah Makineleri Kontrolleri'
      ]
    },
    {
      icon: Eye,
      title: 'Denetim ve Gözetim Hizmetleri',
      description: 'Profesyonel denetim ve gözetim çözümleri',
      color: 'bg-purple-500',
      services: [
        'Yükleme Öncesi Gözetim Hizmetleri',
        'İkinci Taraf Denetim Hizmetleri',
        'Hijyen ve Sanitasyon Denetimleri',
        'Tedarikçi Denetimleri',
        'Süreç Denetimleri',
        'Ürün Denetimleri'
      ]
    },
    {
      icon: Zap,
      title: 'Enerji Verimliliği',
      description: 'Enerji yönetimi ve verimlilik danışmanlığı',
      color: 'bg-yellow-500',
      services: [
        'Enerji Kimlik Belgesi Hazırlama',
        'Enerji Etüdü ve Analizi',
        'Enerji Verimliliği Danışmanlığı',
        'Sıfır Atık Süreç Yönetimi',
        'Karbon Ayak İzi Hesaplama',
        'Yeşil Bina Sertifikasyonu'
      ]
    },
    {
      icon: Award,
      title: 'Özel Denetimler',
      description: 'Sektöre özel denetim ve değerlendirme hizmetleri',
      color: 'bg-red-500',
      services: [
        'Okul Güvenliği Denetimleri',
        'Yeşil Bina Değerlendirmeleri',
        'Otel Denetimleri ve Sınıflandırma',
        'Spor Tesisleri Güvenlik Denetimleri',
        'Hastane Akreditasyon Denetimleri',
        'Gıda Güvenliği Denetimleri'
      ]
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Başvuru',
      description: 'Hizmet talebinizi iletmeniz ve ön değerlendirme yapılması'
    },
    {
      step: '02',
      title: 'Teklif',
      description: 'Detaylı analiz sonrası size özel teklif hazırlanması'
    },
    {
      step: '03',
      title: 'Sözleşme',
      description: 'Hizmet kapsamının belirlenmesi ve sözleşme imzalanması'
    },
    {
      step: '04',
      title: 'Denetim',
      description: 'Uzman ekibimiz tarafından sahada denetim gerçekleştirilmesi'
    },
    {
      step: '05',
      title: 'Rapor',
      description: 'Denetim sonuçlarının raporlanması ve önerilerin sunulması'
    },
    {
      step: '06',
      title: 'Sertifika',
      description: 'Uygunluk durumunda sertifika düzenlenmesi'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-6">Hizmetlerimiz</h1>
              <p className="text-xl leading-relaxed">
                IQCERT olarak, belgelendirme, denetim ve danışmanlık alanlarında 
                geniş bir hizmet yelpazesi sunuyoruz. Uzman kadromuz ve deneyimimizle 
                işletmenizin ihtiyaçlarına en uygun çözümleri sağlıyoruz.
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              {serviceCategories.map((category, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className={`${category.color} p-6`}>
                    <div className="flex items-center text-white">
                      <category.icon className="w-8 h-8 mr-4" />
                      <div>
                        <h3 className="text-2xl font-bold">{category.title}</h3>
                        <p className="text-white/90 mt-2">{category.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3">
                      {category.services.map((service, serviceIndex) => (
                        <li key={serviceIndex} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{service}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6">
                      <button className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium">
                        Detaylı Bilgi Al
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Hizmet Sürecimiz</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Profesyonel yaklaşımımız ve sistematik süreçlerimizle size en iyi hizmeti sunuyoruz
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Neden IQCERT?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Sektördeki deneyimimiz ve uzman kadromuzla fark yaratıyoruz
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Güvenilirlik</h3>
                <p className="text-gray-600">15 yılı aşkın deneyimimizle güvenilir hizmet</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Kalite</h3>
                <p className="text-gray-600">Uluslararası standartlarda kaliteli hizmet</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Hızlı Çözüm</h3>
                <p className="text-gray-600">Hızlı ve etkin çözümlerle zaman tasarrufu</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Uzman Kadro</h3>
                <p className="text-gray-600">Alanında uzman ve deneyimli personel</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-red-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-6">Hizmetlerimiz Hakkında Bilgi Almak İster misiniz?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Uzman ekibimiz size en uygun çözümleri sunmak için hazır. 
              Detaylı bilgi almak ve teklif talep etmek için bizimle iletişime geçin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors">
                Teklif Al
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white hover:text-red-600 transition-colors">
                İletişime Geç
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
